import { useState, useEffect } from 'react'
import { useNavigate, Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'

const WHITELIST_USERNAMES = ['lbj4234', 'davidrheee']
const GATE_PASSWORD = '254729'
const GATE_SESSION_KEY = 'analyze_gate_passed'

/**
 * 보안 메뉴 진입 게이트.
 *
 * 보호 대상:
 * - 화이트리스트 외 사용자(슈퍼관리자/마스터 포함)는 메인으로 즉시 redirect.
 * - 화이트리스트 사용자라도 비밀번호 입력해야 통과.
 * - 통과 후 sessionStorage 에 플래그 저장 → 같은 탭에서 admin 페이지 직접 접근 가능.
 * - 탭 닫으면 다시 입력해야 함.
 *
 * 통과 후 /settings/admin/monthly 로 navigate.
 */
export default function Analyze() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [pw, setPw] = useState('')
  const [error, setError] = useState('')

  // 화이트리스트 검사 — 마스터/슈퍼관리자도 차단
  if (!user || !user.username || !WHITELIST_USERNAMES.includes(user.username)) {
    return <Navigate to="/orders" replace />
  }

  // 자동 포커스
  useEffect(() => {
    const input = document.getElementById('analyze-pw')
    if (input) (input as HTMLInputElement).focus()
  }, [])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (pw === GATE_PASSWORD) {
      sessionStorage.setItem(GATE_SESSION_KEY, '1')
      navigate('/settings/admin/monthly', { replace: true })
    } else {
      setError('비밀번호가 일치하지 않습니다.')
      setPw('')
    }
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0f1116 0%, #1a1d24 100%)',
        margin: 0,
      }}
    >
      <div
        style={{
          background: '#1a1d24',
          padding: '36px 32px',
          borderRadius: 12,
          boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
          width: 360,
          border: '1px solid #2a2f3a',
          color: '#e6e8ee',
        }}
      >
        <div style={{ fontSize: 32, textAlign: 'center', marginBottom: 8 }}>🔒</div>
        <h1
          style={{
            margin: '0 0 6px',
            fontSize: 18,
            fontWeight: 600,
            textAlign: 'center',
            color: '#e6e8ee',
          }}
        >
          추가 인증이 필요합니다
        </h1>
        <p style={{ margin: '0 0 24px', color: '#9aa3b2', fontSize: 13, textAlign: 'center' }}>
          접속 비밀번호를 입력해 주세요.
        </p>

        <form onSubmit={handleSubmit} autoComplete="off">
          <label
            htmlFor="analyze-pw"
            style={{ display: 'block', fontSize: 12, color: '#9aa3b2', marginBottom: 6 }}
          >
            비밀번호
          </label>
          <input
            id="analyze-pw"
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            autoComplete="off"
            required
            style={{
              width: '100%',
              padding: '11px 12px',
              fontSize: 16,
              border: '1px solid #333a47',
              background: '#0f1116',
              color: '#e6e8ee',
              borderRadius: 6,
              boxSizing: 'border-box',
              outline: 'none',
            }}
          />
          <button
            type="submit"
            style={{
              width: '100%',
              padding: 11,
              marginTop: 14,
              background: '#1677ff',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            확인
          </button>
          {error && (
            <div
              style={{
                marginTop: 12,
                padding: '10px 12px',
                background: 'rgba(255,77,79,0.1)',
                color: '#ff7875',
                border: '1px solid rgba(255,77,79,0.3)',
                borderRadius: 6,
                fontSize: 13,
              }}
            >
              {error}
            </div>
          )}
        </form>
        <div
          style={{
            marginTop: 18,
            textAlign: 'center',
            color: '#5a6577',
            fontSize: 11,
          }}
        >
          권한이 없는 접근은 자동으로 차단됩니다.
        </div>
      </div>
    </div>
  )
}

/**
 * /settings/admin/* 페이지를 감싸서 게이트 통과 여부 확인.
 * - 화이트리스트 외 사용자: /orders 로 redirect (조용히)
 * - 게이트 미통과: /analyze 로 redirect
 * - 통과: children 위에 월간/주간 토글 + children 렌더
 */
export function AnalyzeProtected({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  // 화이트리스트 외 → 메인으로 (보안 메뉴 존재 자체를 모르게)
  if (!user || !user.username || !WHITELIST_USERNAMES.includes(user.username)) {
    return <Navigate to="/orders" replace />
  }

  // 게이트 미통과 → 게이트 페이지로
  const passed = sessionStorage.getItem(GATE_SESSION_KEY) === '1'
  if (!passed) {
    return <Navigate to="/analyze" replace />
  }

  const isMonthly = location.pathname.includes('/monthly')
  const isWeekly = location.pathname.includes('/weekly')
  const isBlogLogic = location.pathname.includes('/blog-logic')

  const tabBtn = (active: boolean): React.CSSProperties => ({
    padding: '6px 14px',
    fontSize: 13,
    border: '1px solid ' + (active ? '#1677ff' : '#d9d9d9'),
    background: active ? '#1677ff' : '#fff',
    color: active ? '#fff' : '#333',
    borderRadius: 6,
    cursor: 'pointer',
    fontWeight: active ? 600 : 400,
  })

  return (
    <>
      <div
        style={{
          display: 'flex',
          gap: 8,
          padding: '10px 16px',
          background: '#fafafa',
          borderBottom: '1px solid #f0f0f0',
          alignItems: 'center',
        }}
      >
        <span style={{ fontSize: 12, color: '#888', marginRight: 8 }}>🔒</span>
        <button
          type="button"
          style={tabBtn(isMonthly)}
          onClick={() => navigate('/settings/admin/monthly')}
        >
          월간 집계
        </button>
        <button
          type="button"
          style={tabBtn(isWeekly)}
          onClick={() => navigate('/settings/admin/weekly')}
        >
          주간 집계
        </button>
        <button
          type="button"
          style={tabBtn(isBlogLogic)}
          onClick={() => navigate('/settings/admin/blog-logic')}
        >
          블로그 로직
        </button>
      </div>
      {children}
    </>
  )
}
